import { useState } from "react";
import { LogOut, RefreshCw, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AccessDeniedProps {
  permissionCheckFailed?: boolean;
  onRetry: () => Promise<unknown>;
  onExit: () => Promise<void>;
}

export function AccessDenied({ permissionCheckFailed = false, onRetry, onExit }: AccessDeniedProps) {
  const [pendingAction, setPendingAction] = useState<"retry" | "exit" | null>(null);

  const runAction = async (action: "retry" | "exit", callback: () => Promise<unknown>) => {
    setPendingAction(action);
    try {
      await callback();
    } catch {
      // Keep the stable denied state when retry or sign-out cannot complete.
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <main className="flex min-h-screen min-h-[100dvh] items-center justify-center bg-background p-6">
      <section className="w-full max-w-md space-y-6 text-center" aria-labelledby="access-denied-title">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldX className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h1 id="access-denied-title" className="font-display text-2xl font-semibold text-foreground">
            Acesso não permitido
          </h1>
          <p className="text-sm text-muted-foreground">
            {permissionCheckFailed
              ? "Não foi possível confirmar suas permissões. Tente novamente ou saia com segurança."
              : "Seu perfil não tem permissão para acessar esta área."}
          </p>
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => void runAction("exit", onExit)}
            disabled={pendingAction !== null}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sair com segurança
          </Button>
          <Button
            type="button"
            onClick={() => void runAction("retry", onRetry)}
            disabled={pendingAction !== null}
          >
            <RefreshCw className={`h-4 w-4 ${pendingAction === "retry" ? "animate-spin" : ""}`} aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      </section>
    </main>
  );
}